import React, { useCallback, useEffect, useState, useMemo, forwardRef, useImperativeHandle, useRef, memo } from 'react';
import Modal from 'react-native-modal';
import { useWindowDimensions,  Alert, Share, TextInput, KeyboardAvoidingView, Pressable, FlatList, Platform } from 'react-native';
import styles from '@stylesheet';
import Button from '@components/UI/Button/Button';
import LottieView from '@utils/lottieAnimation';
import UserFinder from '../../Global/UserFinder/UserFinder';
import parseByDelimiter from '@utils/parseByDelimiter';
import UserPermissionItem from './components/UserPermissionItem';
import * as Linking from 'expo-linking';
import { useDispatch, useSelector } from 'react-redux';
import { selectUserName } from '@reduxLocal/selectors/userSelectors';
import { DefaultRootState } from '@reduxLocal/persistState';
import toTitleCase from '@hooks/toTitleCase';
import { Text, View, Title, BlurView, StyledModal, StyledModalHandle } from '@constants/Themed';
import { themeSelector } from '@reduxLocal/selectors/themeSelector';
import { useDeviceType } from '@hooks/useDeviceType';

interface ShareConfirmTypes {
  title?: string;
  refreshFunction?: Function;
  item?: any;
  shareFunction?: Function;
}

export type ShareConfirmHandle = {
  openModal: (value) => void;
};

const ShareUserModal = forwardRef<ShareConfirmHandle, ShareConfirmTypes>((props, ref) => {
  const { name, surname } = useSelector(selectUserName);
  const theme = themeSelector();
  const { isTablet } = useDeviceType();
  const [selectedUser, setSelectedUser] = useState([]);
  const [visibleState, setState] = useState(false);
  const [activeItem, setActiveItem] = useState({ title: 'null' });
  const modalRef = useRef<StyledModalHandle>(null);
  const draggedUser = useSelector((state: DefaultRootState) => state.UI.draggedUser);


  useImperativeHandle(ref, () => ({
    openModal: (value) => {
      setActiveItem(value)
      modalRef.current?.openModal?.()
    }
  }));

  useEffect(() => {
    //Initialise with any dragged user if available
    if (draggedUser) {
      console.log(draggedUser, 'dragged user');
      setSelectedUser([{ ...draggedUser, editPermission: 'common' }])
    } else {
      setSelectedUser([])
    }
  }, [props.item])

  const handleShareFunction = async () => {
    const editList = selectedUser.map(user => { console.log(user); return { id: user.value, editPermission: user.editPermission } });
    if (props.shareFunction) {
      const res = await props.shareFunction({ id: props.item ? props.item._id : (activeItem as any)._id, users: editList });
      if (res.status === 203) {
        modalRef.current?.closeModal?.();
      }
    }
  }

  const onShare = async () => {
    const url = Linking.createURL(``)

    try {
      const result = await Share.share({
        message: `${toTitleCase(name)} ${toTitleCase(surname)} has shared a form with you! \n\n${parseByDelimiter(props.item ? props.item.dataTitle : activeItem.title)} \n\n${url}`,
        url: url,
        title: 'SmartSaaS'
      }, {
        dialogTitle: 'Share this post',
      });
      if (result.action === Share.sharedAction) {
        if (result.activityType) {
          // shared with activity type of result.activityType
        } else {
          // shared
        }
      } else if (result.action === Share.dismissedAction) {
        // dismissed
      }
    } catch (error: any) {
      Alert.alert(error.message);
    }
  };

  const permissionCallback = useCallback(
    (user, permission) => {
      setSelectedUser((prevSelectedUser) => {
        const userIndex = prevSelectedUser.findIndex((selected) => selected.id === user.id);

        if (userIndex !== -1) {
          const updatedSelectedUser = [...prevSelectedUser];
          updatedSelectedUser[userIndex].editPermission = permission;
          return updatedSelectedUser;
        }

        // If user not found, return the previous state
        return prevSelectedUser;
      });
    },
    [setSelectedUser]
  );


  const handleClose = () => { modalRef.current!.closeModal() }


  console.log('selected user', selectedUser);
  return (
    <StyledModal
      ref={modalRef}
      onModalWillHide={() => {
        setSelectedUser([]);
        setActiveItem({ title: 'null' });
        setState(false);
      }}
      title={`Sharing ${activeItem?.title ? parseByDelimiter(activeItem?.title) : 'null'}`}
      caption={`If selected users are not already registered with SmartSaaS, we will send an invitation to the app on your behalf, just input their email.`}
    >
      <View style={{
        flex: 1,
        gap: 15,
        paddingHorizontal: Platform.select({ web: 20, default: 15 }),
        paddingVertical: 10
      }}>
        <KeyboardAvoidingView
          behavior='padding'
          keyboardVerticalOffset={100}
          style={{ flex: 1, zIndex: 100, maxWidth: '100%' }}
        >
          <View style={{
            flex: 1,
            maxWidth: '100%',
            flexDirection: 'column',
            marginBottom: 15
          }}>
            <Text style={{
              fontSize: 16,
              fontWeight: '600',
              marginBottom: 10,
            }}>
              Select Users to Share With:
            </Text>
            <UserFinder
              forceRow={false}
              hideList
              setCallback={setSelectedUser}
            />
          </View >
        </KeyboardAvoidingView>

        {selectedUser.length > 0 ? (
          <View style={{ flex: 2 }}>
            <View style={{
              paddingHorizontal: 15,
              paddingVertical: 10,
              flexDirection: 'row',
              alignSelf: 'stretch',
              borderBottomWidth: 1,
              marginBottom: 10
            }}>
              <Text style={{
                flex: 1,
                fontWeight: '600',
                fontSize: 16,
              }}>
                User
              </Text>
              <Text style={{
                fontWeight: '600',
                fontSize: 16,
                minWidth: 80,
                textAlign: 'center'
              }}>
                Permission
              </Text>
            </View>

            <FlatList
              style={{
                flex: 1,
                paddingHorizontal: 10,
                maxHeight: isTablet ? 300 : 250
              }}
              contentContainerStyle={{ gap: 8 }}
              data={selectedUser}
              showsVerticalScrollIndicator={true}
              renderItem={({ item, index }) => (
                <UserPermissionItem
                  item={item}
                  index={index}
                  callback={permissionCallback}
                />
              )}
            />
          </View>
        ) : (
          <View style={{
            flex: 1,
            alignItems: 'center',
            justifyContent: 'center',
            paddingVertical: 40
          }}>
            <Text style={{
              fontSize: 16,
              opacity: 0.7,
              textAlign: 'center'
            }}>
              Select users above to start sharing
            </Text>
          </View>
        )}

        <View style={{
          flexDirection: 'row',
          gap: 12,
          paddingVertical: 10,
          paddingHorizontal: 5,
          alignItems: 'center',
          justifyContent: 'space-between'
        }}>
          {Platform.OS !== 'web' && (
            <Button
              onPress={onShare}
              iconOnly
              icon={"share-alt"}
              customStyle={{
                borderRadius: 12,
                shadowOpacity: 0,
                minWidth: 50,
                minHeight: 50,
              }}
            />
          )}

          <Button
            disabled={selectedUser.length === 0}
            onPress={handleShareFunction}
            buttonText='Send Invites'
            customStyle={{
              shadowOpacity: 0,
              flex: Platform.OS === 'web' ? 1 : 0,
              minWidth: Platform.OS === 'web' ? undefined : 120,
              borderRadius: 12,
              paddingHorizontal: 20,
              minHeight: 50,
            }}
          />
        </View>
      </View>
    </StyledModal>
  )
})

export default memo(ShareUserModal);
